import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../AppIcon';
import Button from '../../ui/Button';
import Header from '../../ui/Header';
import Sidebar from '../../ui/Sidebar';
import MetricsOverview from './components/MetricsOverview';
import PerformanceChart from './components/PerformanceChart';
import RegionalInsights from './components/RegionalInsights';
import CustomDashboard from './components/CustomDashboard';
import ReportGenerator from './components/ReportGenerator';
import PredictiveAnalytics from './components/PredictiveAnalytics';

const AnalyticsInsightsCenter = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'Overview', icon: 'BarChart3', description: 'Key metrics and performance summary' },
    { id: 'regional', name: 'Regional', icon: 'Globe', description: 'Geographic performance analysis' },
    { id: 'predictive', name: 'Predictive', icon: 'TrendingUp', description: 'AI-powered forecasting and trends' },
    { id: 'custom', name: 'Custom Dashboard', icon: 'LayoutDashboard', description: 'Personalized analytics view' },
    { id: 'reports', name: 'Reports', icon: 'FileText', description: 'Generate comprehensive reports' }
  ];

  const quickActions = [
    { name: 'Export Data', icon: 'Download', action: () => console.log('Export data') },
    { name: 'Schedule Report', icon: 'Calendar', action: () => console.log('Schedule report') },
    { name: 'Share Insights', icon: 'Share', action: () => console.log('Share insights') },
    { name: 'API Access', icon: 'Code', action: () => console.log('API access') }
  ];

  const recentAlerts = [
    {
      id: 1,
      type: 'success',
      title: 'Revenue Target Exceeded',
      message: 'Q4 revenue has surpassed target by 12.3%',
      timestamp: '2 hours ago',
      icon: 'TrendingUp'
    },
    {
      id: 2,
      type: 'warning',
      title: 'Conversion Rate Decline',
      message: 'FinTech sector showing 5% conversion drop',
      timestamp: '4 hours ago',
      icon: 'AlertTriangle'
    },
    {
      id: 3,
      type: 'info',
      title: 'New Market Opportunity',
      message: 'Asia-Pacific region shows 40% growth potential',
      timestamp: '6 hours ago',
      icon: 'Globe'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <MetricsOverview />
            <PerformanceChart />
          </div>
        );
      case 'regional':
        return <RegionalInsights />;
      case 'predictive':
        return <PredictiveAnalytics />;
      case 'custom':
        return <CustomDashboard />;
      case 'reports':
        return <ReportGenerator />;
      default:
        return (
          <div className="space-y-6">
            <MetricsOverview />
            <PerformanceChart />
          </div>
        );
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'success': return 'border-l-success bg-success/5';
      case 'warning': return 'border-l-warning bg-warning/5';
      case 'error': return 'border-l-destructive bg-destructive/5';
      default: return 'border-l-primary bg-primary/5';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} pt-16`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-4 lg:mb-0">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                    <Icon name="BarChart3" size={24} color="white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">Analytics & Insights Center</h1>
                    <p className="text-muted-foreground">Cross-platform intelligence for strategic decision-making</p>
                  </div>
                </div>
                
                {/* Breadcrumb */}
                <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Link to="/unified-landing-hub" className="hover:text-foreground transition-colors duration-200">
                    ScoutBridge
                  </Link>
                  <Icon name="ChevronRight" size={16} />
                  <span className="text-foreground">Analytics Center</span>
                </nav>
              </div>

              <div className="flex items-center space-x-3">
                {quickActions?.map((action, index) => (
                  <Button
                    key={action?.name}
                    variant={index === 0 ? "default" : "outline"}
                    size="sm"
                    onClick={action?.action}
                  >
                    <Icon name={action?.icon} size={16} className="mr-2" />
                    <span className="hidden sm:inline">{action?.name}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="xl:col-span-3">
              {/* Tab Navigation */}
              <div className="bg-card border border-border rounded-lg mb-6">
                <div className="flex flex-wrap border-b border-border">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-all duration-200 border-b-2 ${
                        activeTab === tab?.id
                          ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span className="hidden sm:inline">{tab?.name}</span>
                    </button>
                  ))}
                </div>
                
                {/* Tab Description */}
                <div className="p-4 bg-muted/30">
                  <p className="text-sm text-muted-foreground">
                    {tabs?.find(tab => tab?.id === activeTab)?.description}
                  </p>
                </div>
              </div>

              {/* Tab Content */}
              {renderTabContent()}
            </div>

            {/* Sidebar Content */}
            <div className="xl:col-span-1 space-y-6">
              {/* Real-time Alerts */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-foreground">Recent Alerts</h3>
                  <Button variant="ghost" size="sm">
                    <Icon name="Settings" size={16} />
                  </Button>
                </div>

                <div className="space-y-3">
                  {recentAlerts?.map((alert) => (
                    <div
                      key={alert?.id}
                      className={`p-3 rounded-lg border-l-4 ${getAlertColor(alert?.type)}`}
                    >
                      <div className="flex items-start space-x-2">
                        <Icon name={alert?.icon} size={16} className="mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground mb-1">
                            {alert?.title}
                          </h4>
                          <p className="text-xs text-muted-foreground mb-2">
                            {alert?.message}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {alert?.timestamp}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <Button variant="outline" size="sm" fullWidth className="mt-4">
                  <Icon name="Bell" size={16} className="mr-2" />
                  View All Alerts
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold text-foreground mb-4">Quick Stats</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="Activity" size={16} className="text-primary" />
                      <span className="text-sm text-muted-foreground">Live Users</span>
                    </div>
                    <span className="font-semibold text-foreground">1,247</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="Zap" size={16} className="text-accent" />
                      <span className="text-sm text-muted-foreground">Active Sessions</span>
                    </div>
                    <span className="font-semibold text-foreground">892</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="Database" size={16} className="text-success" />
                      <span className="text-sm text-muted-foreground">Data Points</span>
                    </div>
                    <span className="font-semibold text-foreground">2.4M</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="Clock" size={16} className="text-warning" />
                      <span className="text-sm text-muted-foreground">Avg Response</span>
                    </div>
                    <span className="font-semibold text-foreground">127ms</span>
                  </div>
                </div>
              </div>

              {/* Navigation Links */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold text-foreground mb-4">Related Tools</h3>
                
                <div className="space-y-2">
                  <Link
                    to="/multi-role-dashboard"
                    className="flex items-center space-x-3 p-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
                  >
                    <Icon name="LayoutDashboard" size={16} />
                    <span>Multi-Role Dashboard</span>
                  </Link>
                  
                  <Link
                    to="/referral-performance-center"
                    className="flex items-center space-x-3 p-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
                  >
                    <Icon name="TrendingUp" size={16} />
                    <span>Performance Center</span>
                  </Link>
                  
                  <Link
                    to="/program-discovery-engine"
                    className="flex items-center space-x-3 p-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
                  >
                    <Icon name="Search" size={16} />
                    <span>Discovery Engine</span>
                  </Link>
                  
                  <Link
                    to="/real-time-scouting-interface"
                    className="flex items-center space-x-3 p-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
                  >
                    <Icon name="Radar" size={16} />
                    <span>Scouting Interface</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsInsightsCenter;